import type MapView from '@arcgis/core/views/MapView';
import DistanceMeasurement2D from '@arcgis/core/widgets/DistanceMeasurement2D';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { AnchorSquareIcon } from '@rsos/assets/icons';
import styled, { Theme } from '@rsos/theme/styled';
import { SketchPanelExtension } from './SketchPanelExtension';
import { SketchPanelExtensionProps } from './SketchPanelExtension/SketchPanelExtension';

interface SketchButtonProps {
  /** Theme from styled */
  theme: Theme;
  /** Indicates if the button is pressed. $ prefix to prevent it from being passed to DOM. */
  $isPressed: boolean;
}

const SketchButton = styled.button<SketchButtonProps>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme, $isPressed }) =>
    $isPressed ? theme.colors.highlights : theme.colors.background};

  &:hover {
    background-color: ${({ theme }) => theme.colors.buttons.secondary.hover};
  }
`;

interface MeasureLineToolProps
  extends Pick<SketchPanelExtensionProps, 'isSketchVisible'> {
  mapView: MapView;
}

export function MeasureLineTool({
  isSketchVisible,
  mapView,
}: MeasureLineToolProps) {
  const [isToolVisible, setIsToolVisible] = useState(false);
  const measurementWidget = useDistanceMeasurement2DWidget(
    mapView,
    isToolVisible,
  );

  const handleClick = useCallback(
    function handleClickImpl(e: MouseEvent<HTMLButtonElement>) {
      e.preventDefault();

      if (!mapView.ui) {
        throw new Error('MapView UI is not available.');
      }
      if (!measurementWidget) {
        throw new Error('Measurement widget is not initialized.');
      }

      const newIsToolVisible = !isToolVisible;
      if (measurementWidget) {
        measurementWidget.visible = newIsToolVisible;
      }
      setIsToolVisible(newIsToolVisible);
    },
    [isToolVisible, mapView.ui, measurementWidget],
  );

  if (!mapView) {
    throw new Error('MapView is not provided to MeasureLineTool.');
  }

  return (
    <SketchPanelExtension isSketchVisible={isSketchVisible}>
      <SketchButton
        onClick={handleClick}
        data-name="MeasureLineToolButton"
        title="Measure Line Tool"
        role="button"
        aria-label="Measure Line Tool"
        aria-pressed={isToolVisible}
        $isPressed={isToolVisible}
        tabIndex={0}
      >
        <AnchorSquareIcon width={16} height={16} color="primaryText" />
      </SketchButton>
    </SketchPanelExtension>
  );
}

/**
 * Determines the preferred measurement unit based on the user's browser locale.
 * Returns imperial for US-based locales, metric for all others.
 */
function getPreferredMeasurementUnit(): 'feet' | 'meters' {
  const locale = navigator.language || navigator.languages?.[0] || 'en-US';

  // apparently US, Liberia, and Myanmar primarily use imperial/US customary units. We can adjust this or pull it from preferences later if needed.
  const isUSLocale = locale.startsWith('en-US') || locale.startsWith('en_US');

  return isUSLocale ? 'feet' : 'meters';
}

/**
 * A hook to create and manage the lifecycle of a @see DistanceMeasurement2D widget for the specified mapView. It will be added to the mapView's UI.
 * @returns The DistanceMeasurement2D widget instance if it can be created.
 */
function useDistanceMeasurement2DWidget(
  mapView: MapView,
  isToolVisible: boolean,
) {
  const [measurementWidget, setMeasurementWidget] = useState<
    DistanceMeasurement2D | undefined
  >(undefined);

  useEffect(() => {
    const widget = new DistanceMeasurement2D({
      view: mapView,
      unit: getPreferredMeasurementUnit(),
    });
    // We may create this widget before the SketchWidget is visible, so default to hidden.
    // Users also control visibility of the SketchWidget independently of the tool's visibility, so we use a separate effect to manage visibility.
    widget.visible = false;
    mapView.ui.add(widget, 'top-leading');
    setMeasurementWidget(widget);

    // Cleanup function has access to the widget created in this effect run
    return () => {
      widget.destroy();
      setMeasurementWidget(undefined);
    };
  }, [mapView]);

  // control visibility of the measurement widget based on the SketchWidget and tool visibility
  useEffect(() => {
    if (measurementWidget && measurementWidget.visible !== isToolVisible) {
      measurementWidget.visible = isToolVisible;
    }
  }, [measurementWidget, isToolVisible]);

  return measurementWidget;
}
